// src/poses/norwegianTargets.js
// ----------------------------------------------------------------------------
// Feature-driven targets (canonical) + SVG render compatibility layer
//
// NEW RENDER MODEL (timeline-first):
//   target.render = {
//     timeline: Array<{
//       show: string[],
//       dur: number, // ms (UI timing, explicit per frame)
//       burst?: "strong"|"weak",
//       voicing?: boolean,
//       phase?: string, // optional debug label
//     }>
//   }
//
// Guarantees:
// - All entries are guaranteed to have `render.timeline` so ArticulationSideView never crashes.
// - Stops explicitly define per-frame durations (no inference).
// - Non-stops get a conservative fallback timeline + default durations.
// - Feature fields remain unchanged.
// ----------------------------------------------------------------------------

// --- stops (durations aligned with agreed model) ----------------------------
// Model:
// - rest      = approach into closure (40–70ms)
// - closure   = closure hold
// - burst     = one-shot airflow/voicing window (10–20ms)
// - transition= post-release shaping into next segment (40–90ms)
// - release   = how long the RELEASE STAGE stays on screen in the renderer
//              (today: we approximate releaseStage = burst + transition)

const STOP_DUR = {
  voiceless: {
    rest: 55,        // approach
    closure: 75,     // hold
    burst: 14,       // one-shot effect window
    transition: 70,  // into vowel
    release: 84,     // = burst + transition (stage duration)
  },
  voiced: {
    rest: 55,        // approach
    closure: 50,     // shorter hold
    burst: 12,       // one-shot effect window
    transition: 60,  // into vowel
    release: 72,     // = burst + transition (stage duration)
  },
};

// For bilabials where you want *two* approach frames (rest split explicitly)
const STOP_REST_SPLIT = {
  a: 30,
  b: 25, // a + b = 55
};

// ✅ UI-only settle frame before loop restarts (keeps loop from pausing on release)
const STOP_RETURN_REST_DUR = 90;

// --- Non-stop defaults (UI display timing, not phonetic duration) ----------
const NONSTOP_DUR = {
  consonant: { static: 180, rest: 90 },
  vowelShort: { static: 220, rest: 90 },
  vowelLong: { static: 320, rest: 90 },
  diphthong: { static: 360, rest: 90 },
};

// Fixed, explicit UI split for diphthong glide frames (not inferred per-target)
const DIPHTHONG_SPLIT = {
  start: 0.55,
  end: 0.45,
};

// ---- Canonical feature definitions (yours) --------------------------------
const RAW = {
  // --- STOPS (timeline is explicit, per-frame `dur`) ------------------------

  p: {
    render: {
      timeline: [
        {
          phase: "approach_1",
          show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open"],
          dur: STOP_REST_SPLIT.a,
        },
        {
          phase: "approach_2",
          show: ["upper_jaw_lips_open_velum_raised", "lower_jaw_lips_open"],
          dur: STOP_REST_SPLIT.b,
        },
        {
          phase: "closure",
          show: ["upper_jaw_lips_closed_velum_raised", "lower_jaw_lips_closed"],
          dur: STOP_DUR.voiceless.closure,
        },
        {
          phase: "release",
          show: ["upper_jaw_lips_open_velum_raised", "lower_jaw_lips_open"],
          dur: STOP_DUR.voiceless.release,
          burst: "strong",
          voicing: false,
        },
        // ✅ NEW: settle back to neutral rest before the loop repeats
        {
          phase: "return_rest",
          show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open"],
          dur: STOP_RETURN_REST_DUR,
        },
      ],
    },
    lips: "closed",
    velum: "raised",
    tip: "neutral",
    body: "neutral",
    jaw: "neutral",
    timing: {
      burst: "strong",
      voicing: "off",
      durations: { ...STOP_DUR.voiceless },
    },
  },

  b: {
    render: {
      timeline: [
        {
          phase: "approach_1",
          show: [
            "upper_jaw_lips_open_velum_rest",
            "lower_jaw_lips_open",
            "vocal_folds_open",
          ],
          dur: STOP_REST_SPLIT.a,
        },
        {
          phase: "approach_2",
          show: [
            "upper_jaw_lips_open_velum_raised",
            "lower_jaw_lips_open",
            "vocal_folds_open",
          ],
          dur: STOP_REST_SPLIT.b,
        },
        {
          phase: "closure",
          show: [
            "upper_jaw_lips_closed_velum_raised",
            "lower_jaw_lips_closed",
            "vocal_folds_open",
          ],
          dur: STOP_DUR.voiced.closure,
        },
        {
          phase: "release",
          show: [
            "upper_jaw_lips_open_velum_raised",
            "lower_jaw_lips_open",
            "vocal_folds_closed",
          ],
          dur: STOP_DUR.voiced.release,
          burst: "weak",
          voicing: true,
        },
        // ✅ NEW: neutral rest (velum rest + folds open) before loop repeats
        {
          phase: "return_rest",
          show: [
            "upper_jaw_lips_open_velum_rest",
            "lower_jaw_lips_open",
            "vocal_folds_open",
          ],
          dur: STOP_RETURN_REST_DUR,
        },
      ],
    },
    lips: "closed",
    velum: "raised",
    tip: "neutral",
    body: "neutral",
    jaw: "neutral",
    timing: {
      burst: "weak",
      voicing: "on",
      durations: { ...STOP_DUR.voiced },
    },
  },

  t: {
    render: {
      timeline: [
        {
          phase: "approach",
          show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open"],
          dur: STOP_DUR.voiceless.rest,
        },
        {
          phase: "closure",
          show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open_tip_alveolar"],
          dur: STOP_DUR.voiceless.closure,
        },
        {
          phase: "release",
          show: ["upper_jaw_lips_open_velum_raised", "lower_jaw_lips_open"],
          dur: STOP_DUR.voiceless.release,
          burst: "strong",
          voicing: false,
        },
        // ✅ NEW
        {
          phase: "return_rest",
          show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open"],
          dur: STOP_RETURN_REST_DUR,
        },
      ],
    },
    lips: "open",
    velum: "raised",
    tip: "alveolar",
    body: "neutral",
    jaw: "neutral",
    timing: {
      burst: "strong",
      voicing: "off",
      durations: { ...STOP_DUR.voiceless },
    },
  },

  d: {
    render: {
      timeline: [
        {
          phase: "approach",
          show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open", "vocal_folds_open"],
          dur: STOP_DUR.voiced.rest,
        },
        {
          phase: "closure",
          show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open_tip_alveolar", "vocal_folds_open"],
          dur: STOP_DUR.voiced.closure,
        },
        {
          phase: "release",
          show: ["upper_jaw_lips_open_velum_raised", "lower_jaw_lips_open", "vocal_folds_closed"],
          dur: STOP_DUR.voiced.release,
          burst: "weak",
          voicing: true,
        },
        // ✅ NEW
        {
          phase: "return_rest",
          show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open", "vocal_folds_open"],
          dur: STOP_RETURN_REST_DUR,
        },
      ],
    },
    lips: "open",
    velum: "raised",
    tip: "alveolar",
    body: "neutral",
    jaw: "neutral",
    timing: {
      burst: "weak",
      voicing: "on",
      durations: { ...STOP_DUR.voiced },
    },
  },

  k: {
    render: {
      timeline: [
        {
          phase: "approach",
          show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open"],
          dur: STOP_DUR.voiceless.rest,
        },
        {
          phase: "closure",
          show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open_tongue_back_high"],
          dur: STOP_DUR.voiceless.closure,
        },
        {
          phase: "release",
          show: ["upper_jaw_lips_open_velum_raised", "lower_jaw_lips_open"],
          dur: STOP_DUR.voiceless.release,
          burst: "strong",
          voicing: false,
        },
        // ✅ NEW
        {
          phase: "return_rest",
          show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open"],
          dur: STOP_RETURN_REST_DUR,
        },
      ],
    },
    lips: "open",
    velum: "raised",
    tip: "neutral",
    body: "back_high",
    jaw: "neutral",
    timing: {
      burst: "strong",
      voicing: "off",
      durations: { ...STOP_DUR.voiceless },
    },
  },

  "ɡ": {
    render: {
      timeline: [
        {
          phase: "approach",
          show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open", "vocal_folds_open"],
          dur: STOP_DUR.voiced.rest,
        },
        {
          phase: "closure",
          show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open_tongue_back_high", "vocal_folds_open"],
          dur: STOP_DUR.voiced.closure,
        },
        {
          phase: "release",
          show: ["upper_jaw_lips_open_velum_raised", "lower_jaw_lips_open", "vocal_folds_closed"],
          dur: STOP_DUR.voiced.release,
          burst: "weak",
          voicing: true,
        },
        // ✅ NEW
        {
          phase: "return_rest",
          show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open", "vocal_folds_open"],
          dur: STOP_RETURN_REST_DUR,
        },
      ],
    },
    lips: "open",
    velum: "raised",
    tip: "neutral",
    body: "back_high",
    jaw: "neutral",
    timing: {
      burst: "weak",
      voicing: "on",
      durations: { ...STOP_DUR.voiced },
    },
  },

  // --- NASALS ---------------------------------------------------------------
  m: { lips: "closed", velum: "lowered", tip: "neutral", body: "neutral", jaw: "neutral", timing: { voicing: "on" } },
  n: { lips: "open", velum: "lowered", tip: "alveolar", body: "neutral", jaw: "neutral", timing: { voicing: "on" } },
  "ŋ": { lips: "open", velum: "lowered", tip: "neutral", body: "back_high", jaw: "neutral", timing: { voicing: "on" } },

  // --- FRICATIVES / APPROXIMANTS -------------------------------------------
  f: { lips: "open", velum: "raised", tip: "neutral", body: "neutral", jaw: "neutral" },
  "ʋ": { lips: "open", velum: "raised", tip: "neutral", body: "neutral", jaw: "neutral", timing: { voicing: "on" } },
  s: { lips: "open", velum: "raised", tip: "alveolar", body: "neutral", jaw: "neutral" },
  "ʃ": { lips: "open", velum: "raised", tip: "retroflex", body: "neutral", jaw: "neutral" },
  "ç": { lips: "open", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral" },
  j: { lips: "open", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral", timing: { voicing: "on" } },
  h: { lips: "open", velum: "raised", tip: "neutral", body: "neutral", jaw: "neutral" },

  // --- LIQUIDS --------------------------------------------------------------
  r: { lips: "open", velum: "raised", tip: "alveolar", body: "neutral", jaw: "neutral", timing: { voicing: "on" } },
  l: { lips: "open", velum: "raised", tip: "alveolar", body: "neutral", jaw: "neutral", timing: { voicing: "on" } },

  // --- RETROFLEX SERIES -----------------------------------------------------
  "ʈ": { lips: "open", velum: "raised", tip: "retroflex", body: "neutral", jaw: "neutral", timing: { burst: "strong" } },
  "ɖ": { lips: "open", velum: "raised", tip: "retroflex", body: "neutral", jaw: "neutral", timing: { voicing: "on" } },
  "ɳ": { lips: "open", velum: "lowered", tip: "retroflex", body: "neutral", jaw: "neutral", timing: { voicing: "on" } },
  "ɭ": { lips: "open", velum: "raised", tip: "retroflex", body: "neutral", jaw: "neutral", timing: { voicing: "on" } },

  // --- VOWELS (feature-only, for now) --------------------------------------
  i:    { lips: "open", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral" },
  "iː": { lips: "open", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral", timing: { length: "long" } },

  y:    { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral" },
  "yː": { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral", timing: { length: "long" } },

  e:    { lips: "open", velum: "raised", tip: "neutral", body: "front_mid", jaw: "neutral" },
  "eː": { lips: "open", velum: "raised", tip: "neutral", body: "front_mid", jaw: "neutral", timing: { length: "long" } },

  "ø":   { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "front_mid", jaw: "neutral" },
  "øː":  { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "front_mid", jaw: "neutral", timing: { length: "long" } },

  "æ":   { lips: "open", velum: "raised", tip: "neutral", body: "front_low", jaw: "lowered" },
  "æː":  { lips: "open", velum: "raised", tip: "neutral", body: "front_low", jaw: "lowered", timing: { length: "long" } },

  "ɑ":   { lips: "open", velum: "raised", tip: "neutral", body: "back_low", jaw: "lowered" },
  "ɑː":  { lips: "open", velum: "raised", tip: "neutral", body: "back_low", jaw: "lowered", timing: { length: "long" } },

  "ʉ":   { lips: "rounded", lipShape: "retracted", velum: "raised", tip: "neutral", body: "central_high", jaw: "neutral" },
  "ʉː":  { lips: "rounded", lipShape: "retracted", velum: "raised", tip: "neutral", body: "central_high", jaw: "neutral", timing: { length: "long" } },

  u:     { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "back_high", jaw: "neutral" },
  "uː":  { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "back_high", jaw: "neutral", timing: { length: "long" } },

  o:     { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "back_mid", jaw: "neutral" },
  "oː":  { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "back_mid", jaw: "neutral", timing: { length: "long" } },

  // --- DIPHTHONGS -----------------------------------------------------------
  ei: {
    type: "diphthong",
    glide: {
      start: { lips: "open", velum: "raised", tip: "neutral", body: "front_mid",  jaw: "neutral" },
      end:   { lips: "open", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral" },
    },
  },
  "æi": {
    type: "diphthong",
    glide: {
      start: { lips: "open", velum: "raised", tip: "neutral", body: "front_low",  jaw: "lowered" },
      end:   { lips: "open", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral" },
    },
  },
  "ɑi": {
    type: "diphthong",
    glide: {
      start: { lips: "open", velum: "raised", tip: "neutral", body: "back_low",   jaw: "lowered" },
      end:   { lips: "open", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral" },
    },
  },
  "øʉ": {
    type: "diphthong",
    glide: {
      start: { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "front_mid",    jaw: "neutral" },
      end:   { lips: "rounded", lipShape: "retracted", velum: "raised", tip: "neutral", body: "central_high", jaw: "neutral" },
    },
  },
  "æʉ": {
    type: "diphthong",
    glide: {
      start: { lips: "open", velum: "raised", tip: "neutral", body: "front_low", jaw: "lowered" },
      end:   { lips: "rounded", lipShape: "retracted", velum: "raised", tip: "neutral", body: "central_high", jaw: "neutral" },
    },
  },
  "øy": {
    type: "diphthong",
    glide: {
      start: { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "front_mid",  jaw: "neutral" },
      end:   { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral" },
    },
  },
  oy: {
    type: "diphthong",
    glide: {
      start: { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "back_mid",   jaw: "neutral" },
      end:   { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral" },
    },
  },
};

// ---- Render fallback builder (timeline) ------------------------------------

const LAYERS = {
  upper_open_rest:   "upper_jaw_lips_open_velum_rest",
  upper_closed_rest: "upper_jaw_lips_closed_velum_rest",
  upper_open_raised: "upper_jaw_lips_open_velum_raised",
  lower_open:        "lower_jaw_lips_open",
  lower_closed:      "lower_jaw_lips_closed",
};

function inferVoicing(entry) {
  const v = entry?.timing?.voicing;
  return v === "on" || v === true || v === "true";
}

function inferLipClosure(entry) {
  return String(entry?.lips || "").toLowerCase() === "closed";
}

function computeStaticShowFromFeatures(entry) {
  const voicing = inferVoicing(entry);
  const lipsClosed = inferLipClosure(entry);

  const show = lipsClosed
    ? [LAYERS.upper_closed_rest, LAYERS.lower_closed]
    : [LAYERS.upper_open_raised, LAYERS.lower_open];

  return { show, voicing };
}

function ensureDurations(ipa, entry) {
  // If durations already exist (stops, or user-defined), do nothing.
  if (entry?.timing?.durations && typeof entry.timing.durations === "object") {
    return entry.timing.durations;
  }

  const isDiph = entry?.type === "diphthong";
  const isLong = String(ipa || "").includes("ː") || entry?.timing?.length === "long";

  const vowelBase = String(ipa || "").replace("ː", "");
  const isVowel = ["i","y","e","ø","æ","ɑ","ʉ","u","o"].includes(vowelBase);

  if (isDiph) return { ...NONSTOP_DUR.diphthong };
  if (isVowel) return { ...(isLong ? NONSTOP_DUR.vowelLong : NONSTOP_DUR.vowelShort) };
  return { ...NONSTOP_DUR.consonant };
}

function roundMs(n) {
  return Math.max(0, Math.round(Number(n) || 0));
}

function buildFallbackTimeline(ipa, entry) {
  // If author provided an explicit timeline already, keep it.
  const tl = entry?.render?.timeline;
  if (Array.isArray(tl) && tl.length) return tl;

  const d = ensureDurations(ipa, entry);
  const { show: staticShow, voicing } = computeStaticShowFromFeatures(entry);

  // Diphthongs: 1 rest + 2 glide frames (explicit proportions)
  if (entry?.type === "diphthong" && entry?.glide?.start && entry?.glide?.end) {
    const totalStatic = roundMs(d.static ?? NONSTOP_DUR.diphthong.static);
    const restDur = roundMs(d.rest ?? NONSTOP_DUR.diphthong.rest);

    const a = roundMs(totalStatic * DIPHTHONG_SPLIT.start);
    const b = roundMs(totalStatic - a);

    return [
      { phase: "rest", show: staticShow, dur: restDur, ...(voicing ? { voicing: true } : {}) },
      { phase: "glide_start", show: staticShow, dur: a, ...(voicing ? { voicing: true } : {}) },
      { phase: "glide_end", show: staticShow, dur: b, ...(voicing ? { voicing: true } : {}) },
    ];
  }

  // Other non-stops: rest -> static (explicit durations)
  const restDur = roundMs(d.rest ?? NONSTOP_DUR.consonant.rest);
  const staticDur = roundMs(d.static ?? NONSTOP_DUR.consonant.static);

  return [
    { phase: "rest", show: staticShow, dur: restDur, ...(voicing ? { voicing: true } : {}) },
    { phase: "static", show: staticShow, dur: staticDur, ...(voicing ? { voicing: true } : {}) },
  ];
}

// ---- Export: feature-first objects + guaranteed `.render.timeline` + durations ----

export const NO_TARGETS = Object.fromEntries(
  Object.entries(RAW).map(([ipa, entry]) => {
    const safe = { ...(entry || {}) };

    // Ensure timing exists
    safe.timing = { ...(safe.timing || {}) };

    // Ensure durations exist (stops already explicit; non-stops get defaults)
    safe.timing.durations = ensureDurations(ipa, safe);

    // Ensure render.timeline exists (stops explicit; non-stops fallback)
    safe.render = { ...(safe.render || {}) };
    safe.render.timeline = buildFallbackTimeline(ipa, safe);

    // Optional: enforce explicit dur for every frame (to catch mistakes early)
    for (let i = 0; i < safe.render.timeline.length; i++) {
      const f = safe.render.timeline[i];
      if (!f || !Array.isArray(f.show) || typeof f.dur !== "number") {
        throw new Error(`Invalid render.timeline frame for "${ipa}" at index ${i}`);
      }
    }

    return [ipa, safe];
  })
);